import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FaGithub, FaLinkedin, FaEnvelope, FaGlobe, FaPhone } from 'react-icons/fa';
import profileImage from '../assets/images/avatar.jpg';
import getirfinansLogo from '../assets/images/getirfinans.jpeg';
import comodoLogo from '../assets/images/comodo.jpeg';
import cybersoftLogo from '../assets/images/cybersoft.jpeg';
import hacettepeLogo from '../assets/images/hacettepe.jpeg';
import capaLogo from '../assets/images/capa.jpeg';

// PDF-optimized styled components
const PdfContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Poppins', sans-serif;
  color: #2c3e50;
  font-size: 14px; /* Font boyutunu küçültüldü */
`;

const PdfHeader = styled.header`
  text-align: center;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PdfProfileImage = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  margin-bottom: 10px;
  object-fit: cover;
  border: 2px solid rgba(0, 0, 0, 0.1);
`;

const PdfName = styled.h1`
  font-size: 24px;
  margin: 5px 0;
  color: #2c3e50;
`;

const PdfTitle = styled.h2`
  font-size: 18px;
  color: #7f8c8d;
  margin: 5px 0;
  font-weight: 400;
`;

const PdfSocialLinks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
`;

const PdfSocialLink = styled.a`
  color: #2c3e50;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
  text-decoration: none;
`;

const PdfSection = styled.section`
  margin-bottom: 10px;
  width: 100%;
`;

const PdfSectionTitle = styled.h3`
  font-size: 18px;
  color: #2c3e50;
  margin-bottom: 10px;
  border-bottom: 1px solid #3498db;
  padding-bottom: 5px;
`;

const PdfContent = styled.p`
  line-height: 1.4;
  color: #34495e;
  font-size: 12px;
  text-align: justify;
  white-space: pre-line;
`;

const PdfExperienceItem = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const PdfCompanyLogo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 5px;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const PdfExperienceContent = styled.div`
  flex: 1;
`;

const PdfExperienceTitle = styled.h4`
  font-size: 14px;
  color: #2c3e50;
  margin: 0;
`;

const PdfExperienceCompany = styled.p`
  color: #7f8c8d;
  margin: 2px 0;
  font-size: 12px;
`;

const PdfExperienceDate = styled.p`
  color: #95a5a6;
  font-size: 11px;
  margin: 0;
`;

const PdfExperienceLocation = styled.p`
  color: #7f8c8d;
  font-size: 11px;
  margin: 2px 0;
  font-style: italic;
`;

const PdfEducationItem = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const PdfSchoolLogo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 5px;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const PdfEducationContent = styled.div`
  flex: 1;
`;

const PdfEducationTitle = styled.h4`
  font-size: 14px;
  color: #2c3e50;
  margin: 0;
`;

const PdfEducationDescription = styled.p`
  color: #7f8c8d;
  margin: 2px 0;
  font-weight: 500;
  font-size: 12px;
`;

const PdfEducationDate = styled.p`
  color: #95a5a6;
  font-size: 11px;
  margin: 0;
`;

const PdfSkillCategory = styled.div`
  margin-bottom: 15px;
`;

const PdfSkillCategoryTitle = styled.h4`
  color: #2c3e50;
  margin-bottom: 5px;
  font-size: 14px;
`;

const PdfSkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const PdfSkillTag = styled.span`
  background-color: #3498db;
  color: white;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 10px;
`;

const PdfFooter = styled.footer`
  text-align: center;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #ecf0f1;
`;

const PdfCopyright = styled.p`
  color: #95a5a6;
  font-size: 10px;
  margin: 0;
`;

const PdfResume = React.forwardRef(({}, ref) => {
  const { t, i18n } = useTranslation();

  const calculateDuration = useCallback((startDate, endDate = null) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();

    if (months < 0) {
      years--;
      months += 12;
    }

    const yearText = years > 0 ? `${years} ${t('experience.years')}` : '';
    const monthText = months > 0 ? `${months} ${t('experience.months')}` : '';
    const separator = years > 0 && months > 0 ? ' ' : '';

    return `${yearText}${separator}${monthText}`;
  }, [t]);

  const ExperienceDuration = ({ startDate, endDate }) => {
    const duration = useMemo(() => calculateDuration(startDate, endDate), 
      [startDate, endDate]);
    
    return (
      <PdfExperienceDate>
        {(() => {
          const [year, month] = startDate.split('-');
          return `${t(`experience.month_${parseInt(month)}`)} ${year}`;
        })()} - 
        {endDate ? 
          (() => {
            const [year, month] = endDate.split('-');
            return `${t(`experience.month_${parseInt(month)}`)} ${year}`;
          })() : 
          t('experience.current')
        } · {duration}
      </PdfExperienceDate>
    );
  };

// İki kolonlu düzen için yeni stiller
const TwoColumnLayout = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
`;

const LeftColumn = styled.div`
  flex: 0.7; /* %70 genişlik */
  display: flex;
  flex-direction: column;
`;

const RightColumn = styled.div`
  flex: 0.3; /* %30 genişlik */
  display: flex;
  flex-direction: column;
`;

  return (
    <PdfContainer ref={ref}>
      <TwoColumnLayout>
        <RightColumn>
          <PdfHeader>
            <PdfProfileImage src={profileImage} alt="Ali Ömer Şimşek" />
            <PdfName>Ali Ömer Şimşek</PdfName>
            <PdfTitle>{t('header.title')}</PdfTitle>
            <PdfSocialLinks>
              <PdfSocialLink href="https://github.com/aliomersimsek" target="_blank" rel="noopener noreferrer">
                <FaGithub /> github.com/aliomersimsek
              </PdfSocialLink>
              <PdfSocialLink href="https://tr.linkedin.com/in/aliomersimsek" target="_blank" rel="noopener noreferrer">
                <FaLinkedin /> linkedin.com/in/aliomersimsek
              </PdfSocialLink>
              <PdfSocialLink href="mailto:aliomersimsek@gmail.com">
                <FaEnvelope /> aliomersimsek@gmail.com
              </PdfSocialLink>
              <PdfSocialLink href="https://aliomersimsek.com" target="_blank" rel="noopener noreferrer">
                <FaGlobe /> aliomersimsek.com
              </PdfSocialLink>
              <PdfSocialLink href="tel:+905347936192">
                <FaPhone /> +90 (534) 793 6192
              </PdfSocialLink>
            </PdfSocialLinks>
          </PdfHeader>
        </RightColumn>
        <LeftColumn>
          {/* Hakkımda kısmı - tek kolon */}
          <PdfSection>
            <PdfSectionTitle>{t('about.title')}</PdfSectionTitle>
            <PdfContent>{t('about.content')}</PdfContent>
          </PdfSection>
        </LeftColumn>
      </TwoColumnLayout>
      
      {/* İki kolonlu düzen başlangıcı */}
      <TwoColumnLayout>
        {/* Sol kolon - Deneyim ve Eğitim */}
        <LeftColumn>
          <PdfSection>
            <PdfSectionTitle>{t('experience.title')}</PdfSectionTitle>
            <PdfExperienceItem>
              <PdfCompanyLogo src={getirfinansLogo} alt="getirfinans logo" />
              <PdfExperienceContent>
                <PdfExperienceTitle>Software Engineer</PdfExperienceTitle>
                <PdfExperienceCompany>getirfinans</PdfExperienceCompany>
                <ExperienceDuration startDate="2022-03-07" />
                <PdfExperienceLocation>Ankara, Turkey</PdfExperienceLocation>
              </PdfExperienceContent>
            </PdfExperienceItem>

            <PdfExperienceItem>
              <PdfCompanyLogo src={comodoLogo} alt="Comodo logo" />
              <PdfExperienceContent>
                <PdfExperienceTitle>Software Developer</PdfExperienceTitle>
                <PdfExperienceCompany>Comodo</PdfExperienceCompany>
                <ExperienceDuration startDate="2018-08-06" endDate="2022-02-25" />
                <PdfExperienceLocation>Ankara, Turkey</PdfExperienceLocation>
              </PdfExperienceContent>
            </PdfExperienceItem>

            <PdfExperienceItem>
              <PdfCompanyLogo src={cybersoftLogo} alt="Cybersoft logo" />
              <PdfExperienceContent>
                <PdfExperienceTitle>Software Engineer</PdfExperienceTitle>
                <PdfExperienceCompany>Cybersoft</PdfExperienceCompany>
                <ExperienceDuration startDate="2017-02-13" endDate="2018-07-20" />
                <PdfExperienceLocation>Ankara, Turkey</PdfExperienceLocation>
              </PdfExperienceContent>
            </PdfExperienceItem>
          </PdfSection>

          <PdfSection>
            <PdfSectionTitle>{t('education.title')}</PdfSectionTitle>
            <PdfEducationItem>
              <PdfSchoolLogo src={hacettepeLogo} alt="Hacettepe Üniversitesi logo" />
              <PdfEducationContent>
                <PdfEducationTitle>{t('education.hacettepe.name')}</PdfEducationTitle>
                <PdfEducationDescription>{t('education.hacettepe.department')}</PdfEducationDescription>
                <PdfEducationDate>2011 - 2016</PdfEducationDate>
              </PdfEducationContent>
            </PdfEducationItem>

            <PdfEducationItem>
              <PdfSchoolLogo src={capaLogo} alt="Çapa Fen Lisesi logo" />
              <PdfEducationContent>
                <PdfEducationTitle>{t('education.capa.name')}</PdfEducationTitle>
                <PdfEducationDescription>{t('education.capa.department')}</PdfEducationDescription>
                <PdfEducationDate>2006 - 2010</PdfEducationDate>
              </PdfEducationContent>
            </PdfEducationItem>
          </PdfSection>
        </LeftColumn>

        {/* Sağ kolon - Yetenekler */}
        <RightColumn>
          <PdfSection>
            <PdfSectionTitle>{t('skills.title')}</PdfSectionTitle>
            
            <PdfSkillCategory>
              <PdfSkillCategoryTitle>{t('skills.categories.programmingLanguages')}</PdfSkillCategoryTitle>
              <PdfSkillsContainer>
                {['Java', 'JavaScript', 'SQL', 'HTML', 'CSS'].map((skill) => (
                  <PdfSkillTag key={skill}>{skill}</PdfSkillTag>
                ))}
              </PdfSkillsContainer>
            </PdfSkillCategory>

            <PdfSkillCategory>
              <PdfSkillCategoryTitle>{t('skills.categories.frameworksLibraries')}</PdfSkillCategoryTitle>
              <PdfSkillsContainer>
                {['Spring Boot', 'React', 'Node.js', 'Hibernate', 'JPA'].map((skill) => (
                  <PdfSkillTag key={skill}>{skill}</PdfSkillTag>
                ))}
              </PdfSkillsContainer>
            </PdfSkillCategory>

            <PdfSkillCategory>
              <PdfSkillCategoryTitle>{t('skills.categories.awsServices')}</PdfSkillCategoryTitle>
              <PdfSkillsContainer>
                {[
                  'AWS Lambda',
                  'AWS Elastic Beanstalk',
                  'AWS EC2',
                  'AWS S3',
                  'AWS RDS'
                ].map((skill) => (
                  <PdfSkillTag key={skill}>{skill}</PdfSkillTag>
                ))}
              </PdfSkillsContainer>
            </PdfSkillCategory>

            <PdfSkillCategory>
              <PdfSkillCategoryTitle>{t('skills.categories.databases')}</PdfSkillCategoryTitle>
              <PdfSkillsContainer>
                {[
                  'MySQL',
                  'PostgreSQL',
                  'MongoDB',
                  'Redis'
                ].map((skill) => (
                  <PdfSkillTag key={skill}>{skill}</PdfSkillTag>
                ))}
              </PdfSkillsContainer>
            </PdfSkillCategory>

            <PdfSkillCategory>
              <PdfSkillCategoryTitle>{t('skills.categories.devopsTools')}</PdfSkillCategoryTitle>
              <PdfSkillsContainer>
                {[
                  'Docker',
                  'Kubernetes',
                  'Jenkins',
                  'Git',
                  'Terraform'
                ].map((skill) => (
                  <PdfSkillTag key={skill}>{skill}</PdfSkillTag>
                ))}
              </PdfSkillsContainer>
            </PdfSkillCategory>
          </PdfSection>
        </RightColumn>
      </TwoColumnLayout>

      {/*<PdfFooter>
        <PdfCopyright>
          © {new Date().getFullYear()} Ali Ömer Şimşek. {i18n.language === 'tr' ? 'Tüm hakları saklıdır.' : 'All rights reserved.'}
        </PdfCopyright>
      </PdfFooter>*/}
    </PdfContainer>
  );
});

export default PdfResume;
